import * as React from "react"
import { Command as CommandPrimitive } from "cmdk"
import { Search } from "lucide-react"

import { cn } from "../lib/utils"
import { menuItem, menuSurface } from "../styles/recipes.js"
import { Dialog, DialogContent } from "./dialog"

const Command = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    data-slot="command"
    className={cn(
      `flex h-full w-full flex-col overflow-hidden ${menuSurface}`,
      className
    )}
    {...props} />
))
Command.displayName = CommandPrimitive.displayName

const CommandDialog = ({
  children,
  ...props
}) => {
  return (
    (<Dialog {...props}>
      <DialogContent className="overflow-hidden p-0">
        <Command
          className="[&_[cmdk-group-heading]]:px-[var(--ds-space-2)] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-[var(--ds-space-2)] [&_[cmdk-input-wrapper]_svg]:size-[var(--ds-icon-size-md)] [&_[cmdk-input]]:h-[var(--ds-density-control-height-xl)] [&_[cmdk-item]]:px-[var(--ds-space-2)] [&_[cmdk-item]]:py-[var(--ds-space-3)] [&_[cmdk-item]_svg]:size-[var(--ds-icon-size-md)]">
          {children}
        </Command>
      </DialogContent>
    </Dialog>)
  );
}

const CommandInput = React.forwardRef(({ className, ...props }, ref) => (
  <div data-slot="command-input-wrapper" className="flex items-center border-b border-border-subtle px-[var(--ds-space-3)]" cmdk-input-wrapper="">
    <Search className="mr-[var(--ds-space-2)] size-[var(--ds-icon-size-sm)] shrink-0 opacity-50" />
    <CommandPrimitive.Input
      ref={ref}
      data-slot="command-input"
      className={cn(
        "flex h-[var(--ds-density-control-height)] w-full rounded-[var(--ds-radius-control)] bg-transparent py-[var(--ds-space-3)] text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
        className
      )}
      {...props} />
  </div>
))

CommandInput.displayName = CommandPrimitive.Input.displayName

const CommandList = React.forwardRef(({ className, onWheel, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    data-slot="command-list"
    className={cn("max-h-[var(--ds-menu-max-height)] overflow-y-auto overflow-x-hidden [scrollbar-color:var(--ds-scrollbar-thumb)_transparent]", className)}
    onWheel={(event) => {
      // Nested in Dialog/ScrollArea: keep wheel on this list (same pattern as SelectContent).
      onWheel?.(event)
      event.stopPropagation()
    }}
    {...props} />
))

CommandList.displayName = CommandPrimitive.List.displayName

const CommandEmpty = React.forwardRef((props, ref) => (
  <CommandPrimitive.Empty ref={ref} data-slot="command-empty" className="py-[var(--ds-space-6)] text-center text-sm" {...props} />
))

CommandEmpty.displayName = CommandPrimitive.Empty.displayName

const CommandGroup = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    data-slot="command-group"
    className={cn(
      "overflow-hidden p-[var(--ds-space-1)] text-foreground [&_[cmdk-group-heading]]:px-[var(--ds-space-2)] [&_[cmdk-group-heading]]:py-[var(--ds-space-1-5)] [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground",
      className
    )}
    {...props} />
))

CommandGroup.displayName = CommandPrimitive.Group.displayName

const CommandSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator ref={ref} data-slot="command-separator" className={cn("-mx-[var(--ds-space-1)] h-px bg-border", className)} {...props} />
))
CommandSeparator.displayName = CommandPrimitive.Separator.displayName

const CommandItem = React.forwardRef(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    data-slot="command-item"
    className={cn(
      `${menuItem} gap-[var(--ds-space-2)] px-[var(--ds-space-2)] py-[var(--ds-space-1-5)] data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-[var(--ds-icon-size-sm)] [&_svg]:shrink-0`,
      className
    )}
    {...props} />
))

CommandItem.displayName = CommandPrimitive.Item.displayName

const CommandShortcut = ({
  className,
  ...props
}) => {
  return (
    (<span
      data-slot="command-shortcut"
      className={cn("ml-auto text-xs tracking-widest text-muted-foreground", className)}
      {...props} />)
  );
}
CommandShortcut.displayName = "CommandShortcut"

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
}
